import React, { memo, useCallback, useMemo } from 'react'
import { GridColumnVisibilityModel, GridPaginationModel } from '@mui/x-data-grid-pro'
import { useSelector } from 'react-redux'
import { EmptyGigsafeUsers } from 'src/components/gigsafe-user/grid/EmptyGigsafeUsers'
import { GridTable } from 'src/components/shared/grid/GridTable'
import {
  GIGSAFE_USERS_GRID_COLUMNS_STORAGE_KEY,
  gigsafeUserPageSizeOptions,
} from 'src/constants/gigsafe-user/constants'
import { GigsafeUsersTableFields } from 'src/constants/gigsafe-user/enums'
import { buildGigsafeUserColumns } from 'src/constants/gigsafe-user/gigsafeUsersGrid'
import type { GigsafeUser } from 'src/interfaces/gigsafeUser'
import {
  getGigsafeUsers,
  getGigsafeUsersLoading,
  getGigsafeUsersTotal,
} from 'src/selectors/gigsafeUser'
import { useGridColumns } from 'src/utils/hooks/grid/useGridColumns'

interface Props {
  paginationModel: GridPaginationModel
  onPaginationModelChange(model: GridPaginationModel): void
}

const getRowId = (row: GigsafeUser) => {
  return row.id
}

// Name identifies the row; hiding it would leave an unreadable table.
const UNHIDEABLE_COLUMNS: Array<string> = [GigsafeUsersTableFields.name]

const GigsafeUsersTableComponent = ({ paginationModel, onPaginationModelChange }: Props) => {
  const users = useSelector(getGigsafeUsers)
  const usersTotal = useSelector(getGigsafeUsersTotal)
  const isLoading = useSelector(getGigsafeUsersLoading)

  const userColumns = useMemo(() => {
    return buildGigsafeUserColumns()
  }, [])

  const { includedColumns, handleAddExcludedColumn, handleRemoveExcludedColumn } = useGridColumns(
    userColumns,
    GIGSAFE_USERS_GRID_COLUMNS_STORAGE_KEY,
  )

  const columnVisibilityModel = useMemo(() => {
    return userColumns.reduce<GridColumnVisibilityModel>((accumulator, column) => {
      accumulator[column.field] = includedColumns.includes(column.field)

      return accumulator
    }, {})
  }, [userColumns, includedColumns])

  // `useGridColumns` stores excluded fields as a flat list; the grid speaks a visibility model.
  // Translate both ways so menu changes persist to localStorage across reloads.
  const handleColumnVisibilityModelChange = useCallback(
    (model: GridColumnVisibilityModel) => {
      userColumns.forEach((column) => {
        if (UNHIDEABLE_COLUMNS.includes(column.field)) {
          return
        }

        if (model[column.field] === false) {
          handleAddExcludedColumn(column.field)

          return
        }

        handleRemoveExcludedColumn(column.field)
      })
    },
    [userColumns, handleAddExcludedColumn, handleRemoveExcludedColumn],
  )

  // Server pagination: `rowCount` is the envelope's nested `pagination.total`. `GridTable` flips
  // to paginationMode: 'server' on its own once a paginationModel is passed.
  return (
    <GridTable<GigsafeUser>
      rows={users}
      columns={userColumns}
      getRowId={getRowId}
      isLoading={isLoading}
      rowCount={usersTotal}
      paginationModel={paginationModel}
      onPaginationModelChange={onPaginationModelChange}
      pageSizeOptions={gigsafeUserPageSizeOptions}
      columnVisibilityModel={columnVisibilityModel}
      onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
      EmptyElement={EmptyGigsafeUsers}
      tableDataTestId="gigsafe-users-table"
      rowDataTestIdPrefix="gigsafe-users"
    />
  )
}

export const GigsafeUsersTable = memo(GigsafeUsersTableComponent)
